'use client';
import React, { useEffect, useState, useCallback } from 'react';
import MovieCard from '@/components/MovieCard';
import SectionHeader from '@/components/SectionHeader';
import { Film, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { Movie } from '@/lib/mockData';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

export default function LatestMoviesSection() {
  const [latestMovies, setLatestMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchMovies = useCallback(async () => {
    const { data, error } = await supabase
      .from('movies')
      .select('id, title, slug, description, year, imdb_rating, genre, country, language, poster_url, backdrop_url, featured, download_count, download_source_type, file_path, external_download_url, file_size, created_at')
      .order('created_at', { ascending: false })
      .limit(8);
    if (!error && data) setLatestMovies(data as Movie[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMovies();
  }, [fetchMovies]);

  useRealtimeContent({
    tables: ['movies'],
    onAnyChange: () => fetchMovies(),
  });

  if (loading) {
    return (
      <section className="pt-10">
        <SectionHeader title="Latest Movies" subtitle="Freshly added to the library" viewAllHref="/movies-page" accentColor="cyan" />
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (latestMovies.length === 0) {
    return (
      <section className="pt-10">
        <SectionHeader title="Latest Movies" subtitle="Freshly added to the library" viewAllHref="/movies-page" accentColor="cyan" />
        <div className="glass-card border border-border rounded-2xl p-10 text-center">
          <Film size={32} className="text-primary mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground font-body">No movies added yet. Check back soon!</p>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-10">
      <SectionHeader title="Latest Movies" subtitle="Freshly added to the library" viewAllHref="/movies-page" accentColor="cyan" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
        {latestMovies.map((movie) => (
          <MovieCard key={`latest-movie-${movie.id}`} movie={movie} href={`/movie-detail-page?slug=${movie.slug}`} />
        ))}
      </div>
    </section>
  );
}